import { useState, useEffect } from 'react'
import { Button, Input, Label } from '../ui'
import { X, AlertCircle, MapPin, Camera, Clock, Timer, Coffee, LogOut, Navigation } from 'lucide-react'
import type { TeamRuleType, TeamRuleConfig, RestrictClockInGeofenceConfig, RestrictClockInTimeWindowConfig, MaxShiftDurationConfig, MinBreakDurationConfig, RestrictEarlyClockInConfig, AutoClockOutConfig } from '../../types/teamRules'

interface Location {
  id: string
  name: string
}

interface AddRuleModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (ruleData: any) => Promise<void>
  teamId: string
  teamName: string
}

const RULE_TYPES: Array<{
  id: TeamRuleType
  name: string
  description: string
  icon: any
  category: string
}> = [
  {
    id: 'REQUIRE_LOCATION_CLOCK_IN',
    name: 'Require Location for Clock In',
    description: 'Users must select a location to clock in',
    icon: MapPin,
    category: 'Location'
  },
  {
    id: 'REQUIRE_PHOTO_CLOCK_IN',
    name: 'Require Photo Verification',
    description: 'Users must take a photo when clocking in',
    icon: Camera,
    category: 'Verification'
  },
  {
    id: 'RESTRICT_CLOCK_IN_GEOFENCE',
    name: 'Restrict to Geofence',
    description: 'Users can only clock in from approved locations',
    icon: Navigation,
    category: 'Location'
  },
  {
    id: 'RESTRICT_CLOCK_IN_TIME_WINDOW',
    name: 'Restrict Clock In Time',
    description: 'Users can only clock in within scheduled shift window',
    icon: Clock,
    category: 'Time'
  },
  {
    id: 'MAX_SHIFT_DURATION',
    name: 'Maximum Shift Duration',
    description: 'Limit the maximum hours per shift',
    icon: Timer,
    category: 'Time'
  },
  {
    id: 'MIN_BREAK_DURATION',
    name: 'Minimum Break Duration',
    description: 'Enforce minimum break time after working certain hours',
    icon: Coffee,
    category: 'Time'
  },
  {
    id: 'RESTRICT_EARLY_CLOCK_IN',
    name: 'Restrict Early Clock In',
    description: 'Prevent clocking in too early before scheduled shift',
    icon: AlertCircle,
    category: 'Time'
  },
  {
    id: 'AUTO_CLOCK_OUT',
    name: 'Auto Clock Out',
    description: 'Automatically clock out employees after specified duration',
    icon: LogOut,
    category: 'Time'
  }
]

const ACTION_TYPES = [
  { id: 'BLOCK', name: 'Block', description: 'Prevent the action completely', color: 'red' },
  { id: 'WARN', name: 'Warn', description: 'Show a warning but allow the action', color: 'yellow' },
  { id: 'REQUIRE_APPROVAL', name: 'Require Approval', description: 'Require manager approval', color: 'blue' }
]

const SCOPE_TYPES = [
  { id: 'TEAM', name: 'This Team Only', description: 'Apply rule only to this team' },
  { id: 'TEAM_AND_SUBTEAMS', name: 'Team and Sub-teams', description: 'Apply rule to this team and all sub-teams' }
]

export function AddRuleModal({ isOpen, onClose, onSave, teamId, teamName }: AddRuleModalProps) {
  const [step, setStep] = useState(1)
  const [selectedRuleType, setSelectedRuleType] = useState<TeamRuleType | ''>('')
  const [formData, setFormData] = useState<{
    rule_type: TeamRuleType | ''
    scope: 'TEAM' | 'TEAM_AND_SUBTEAMS'
    action: 'BLOCK' | 'WARN' | 'REQUIRE_APPROVAL'
    priority: number
    is_active: boolean
    config: TeamRuleConfig
  }>({
    rule_type: '',
    scope: 'TEAM_AND_SUBTEAMS',
    action: 'BLOCK',
    priority: 0,
    is_active: true,
    config: {}
  })
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchLocations()
    }
  }, [isOpen, teamId])

  const fetchLocations = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/locations?team_id=${teamId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )
      
      if (response.ok) {
        const data = await response.json()
        setLocations(data.locations || [])
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err)
    }
  }

  const handleRuleTypeSelect = (ruleType: TeamRuleType) => {
    setSelectedRuleType(ruleType)
    setFormData(prev => ({
      ...prev,
      rule_type: ruleType,
      config: getDefaultConfig(ruleType)
    }))
    setStep(2)
  }

  const getDefaultConfig = (ruleType: TeamRuleType): TeamRuleConfig => {
    switch (ruleType) {
      case 'REQUIRE_LOCATION_CLOCK_IN':
        return { message: 'You must select a location to clock in' }
      case 'REQUIRE_PHOTO_CLOCK_IN':
        return { message: 'Photo verification is required to clock in' }
      case 'RESTRICT_CLOCK_IN_GEOFENCE':
        const geofenceConfig: RestrictClockInGeofenceConfig = { 
          message: 'You must be at an approved location to clock in',
          geofence_ids: []
        }
        return geofenceConfig
      case 'RESTRICT_CLOCK_IN_TIME_WINDOW':
        const timeWindowConfig: RestrictClockInTimeWindowConfig = { 
          message: 'You can only clock in within your scheduled shift window',
          window_minutes: 15,
          before_shift: true,
          after_shift: true
        }
        return timeWindowConfig
      case 'MAX_SHIFT_DURATION':
        const maxShiftConfig: MaxShiftDurationConfig = { 
          message: 'Shift duration cannot exceed the maximum limit',
          max_hours: 12
        }
        return maxShiftConfig
      case 'MIN_BREAK_DURATION':
        const minBreakConfig: MinBreakDurationConfig = {
          message: 'You must take required breaks',
          min_minutes: 30,
          after_hours: 5
        }
        return minBreakConfig
      case 'RESTRICT_EARLY_CLOCK_IN':
        const earlyClockConfig: RestrictEarlyClockInConfig = {
          message: 'You cannot clock in too early',
          minutes_before_shift: 15
        }
        return earlyClockConfig
      case 'AUTO_CLOCK_OUT':
        const autoClockConfig: AutoClockOutConfig = {
          message: 'You have been automatically clocked out',
          after_hours: 12,
          grace_minutes: 30
        }
        return autoClockConfig
      default:
        return {}
    }
  }

  const handleConfigChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value
      }
    }))
  }

  const validateForm = (): string | null => {
    // Check if rule type is selected
    if (!formData.rule_type) {
      return 'Please select a rule type'
    }

    // Rule-specific validations
    switch (formData.rule_type) {
      case 'RESTRICT_CLOCK_IN_GEOFENCE': {
        const config = formData.config as RestrictClockInGeofenceConfig
        if (!config.geofence_ids || config.geofence_ids.length === 0) {
          return 'Please select at least one location for geofence restriction'
        }
        break
      }
        
      case 'RESTRICT_CLOCK_IN_TIME_WINDOW': {
        const config = formData.config as RestrictClockInTimeWindowConfig
        if (!config.window_minutes || config.window_minutes < 1) {
          return 'Time window must be at least 1 minute'
        }
        if (config.window_minutes > 120) {
          return 'Time window cannot exceed 120 minutes'
        }
        break
      }
        
      case 'MAX_SHIFT_DURATION': {
        const config = formData.config as MaxShiftDurationConfig
        if (!config.max_hours || config.max_hours < 1) {
          return 'Maximum shift duration must be at least 1 hour'
        }
        if (config.max_hours > 24) {
          return 'Maximum shift duration cannot exceed 24 hours'
        }
        break
      }
        
      case 'MIN_BREAK_DURATION': {
        const breakConfig = formData.config as MinBreakDurationConfig
        if (!breakConfig.min_minutes || breakConfig.min_minutes < 1) {
          return 'Minimum break duration must be at least 1 minute'
        }
        if (breakConfig.min_minutes > 120) {
          return 'Minimum break duration cannot exceed 120 minutes'
        }
        if (!breakConfig.after_hours || breakConfig.after_hours < 1) {
          return 'After hours must be at least 1 hour'
        }
        if (breakConfig.after_hours > 12) {
          return 'After hours cannot exceed 12 hours'
        }
        break
      }
        
      case 'RESTRICT_EARLY_CLOCK_IN': {
        const earlyConfig = formData.config as RestrictEarlyClockInConfig
        if (earlyConfig.minutes_before_shift === undefined || earlyConfig.minutes_before_shift < 0) {
          return 'Minutes before shift must be 0 or greater'
        }
        if (earlyConfig.minutes_before_shift > 120) {
          return 'Minutes before shift cannot exceed 120'
        }
        break
      }
        
      case 'AUTO_CLOCK_OUT': {
        const autoConfig = formData.config as AutoClockOutConfig
        if (!autoConfig.after_hours || autoConfig.after_hours < 1) {
          return 'Auto clock-out hours must be at least 1 hour'
        }
        if (autoConfig.after_hours > 24) {
          return 'Auto clock-out hours cannot exceed 24 hours'
        }
        break
      }
    }

    // Validate priority
    if (formData.priority < 0 || formData.priority > 100) {
      return 'Priority must be between 0 and 100'
    }

    return null
  }

  const handleSave = async () => {
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setLoading(true)
      setError(null)
      await onSave(formData)
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setStep(1)
    setSelectedRuleType('')
    setFormData({
      rule_type: '',
      scope: 'TEAM_AND_SUBTEAMS',
      action: 'BLOCK',
      priority: 0,
      is_active: true,
      config: {}
    })
    setError(null)
    onClose()
  }

  const renderRuleConfig = () => {
    const ruleType = formData.rule_type
    
    switch (ruleType) {
      case 'RESTRICT_CLOCK_IN_GEOFENCE': {
        const config = formData.config as RestrictClockInGeofenceConfig
        return (
          <div className="space-y-4">
            <div>
              <Label>Select Allowed Locations <span className="text-red-500">*</span></Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {locations.map(location => (
                  <label key={location.id} className="flex items-center hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={config.geofence_ids?.includes(location.id) || false}
                      onChange={(e) => {
                        const currentIds = config.geofence_ids || []
                        const newIds = e.target.checked
                          ? [...currentIds, location.id]
                          : currentIds.filter((id: string) => id !== location.id)
                        handleConfigChange('geofence_ids', newIds)
                      }}
                      className="mr-3"
                    />
                    <span className="text-sm">{location.name}</span>
                  </label>
                ))}
              </div>
              {locations.length === 0 && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    No locations found. Create locations first to use geofence restrictions.
                  </p>
                </div>
              )}
              {config.geofence_ids && config.geofence_ids.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {config.geofence_ids.length} location(s) selected
                </p>
              )}
            </div>
          </div>
        )
      }
        
      case 'RESTRICT_CLOCK_IN_TIME_WINDOW': {
        const config = formData.config as RestrictClockInTimeWindowConfig
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="window-minutes">Time Window (minutes)</Label>
              <Input
                id="window-minutes"
                type="number"
                min="1"
                max="120"
                value={config.window_minutes || 15}
                onChange={(e) => handleConfigChange('window_minutes', parseInt(e.target.value))}
                placeholder="15"
              />
              <p className="text-sm text-gray-500 mt-1">
                Users can clock in this many minutes before/after their scheduled shift
              </p>
            </div>
          </div>
        )
      }
        
      case 'MAX_SHIFT_DURATION': {
        const config = formData.config as MaxShiftDurationConfig
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="max-hours">Maximum Hours per Shift</Label>
              <Input
                id="max-hours"
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={config.max_hours || 12}
                onChange={(e) => handleConfigChange('max_hours', parseFloat(e.target.value))}
                placeholder="12"
              />
            </div>
          </div>
        )
      }
        
      case 'MIN_BREAK_DURATION':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="min-minutes">Minimum Break Duration (minutes)</Label>
              <Input
                id="min-minutes"
                type="number"
                min="1"
                max="120"
                value={(formData.config as MinBreakDurationConfig).min_minutes || 30}
                onChange={(e) => handleConfigChange('min_minutes', parseInt(e.target.value))}
                placeholder="30"
              />
            </div>
            <div>
              <Label htmlFor="after-hours">After Working Hours</Label>
              <Input
                id="after-hours"
                type="number"
                min="1"
                max="12"
                step="0.5"
                value={(formData.config as MinBreakDurationConfig).after_hours || 5}
                onChange={(e) => handleConfigChange('after_hours', parseFloat(e.target.value))}
                placeholder="5"
              />
              <p className="text-sm text-gray-500 mt-1">
                Break required after working this many hours
              </p>
            </div>
          </div>
        )
        
      case 'RESTRICT_EARLY_CLOCK_IN':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="minutes-before">Minutes Before Shift</Label>
              <Input
                id="minutes-before"
                type="number"
                min="0"
                max="120"
                value={(formData.config as RestrictEarlyClockInConfig).minutes_before_shift || 15}
                onChange={(e) => handleConfigChange('minutes_before_shift', parseInt(e.target.value))}
                placeholder="15"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum minutes employees can clock in before their scheduled shift
              </p>
            </div>
          </div>
        )
        
      case 'AUTO_CLOCK_OUT':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="after-hours">Hours Until Auto Clock-Out</Label>
              <Input
                id="after-hours"
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={(formData.config as AutoClockOutConfig).after_hours || 12}
                onChange={(e) => handleConfigChange('after_hours', parseFloat(e.target.value))}
                placeholder="12"
              />
            </div>
            <div>
              <Label htmlFor="grace-minutes">Grace Period (minutes)</Label>
              <Input
                id="grace-minutes"
                type="number"
                min="0"
                max="60"
                value={(formData.config as AutoClockOutConfig).grace_minutes || 30}
                onChange={(e) => handleConfigChange('grace_minutes', parseInt(e.target.value))}
                placeholder="30"
              />
              <p className="text-sm text-gray-500 mt-1">
                Warning period before automatic clock-out
              </p>
            </div>
          </div>
        )
        
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Add Rule for {teamName}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Step 1: Select Rule Type */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Rule Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {RULE_TYPES.map(ruleType => {
                  const Icon = ruleType.icon
                  return (
                    <button
                      key={ruleType.id}
                      onClick={() => handleRuleTypeSelect(ruleType.id)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="flex items-start">
                        <Icon className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{ruleType.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{ruleType.description}</p>
                          <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {ruleType.category}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 2: Configure Rule */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Configure Rule</h3>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    {RULE_TYPES.find(r => r.id === selectedRuleType)?.description}
                  </p>
                </div>
              </div>

              {/* Rule Scope */}
              <div>
                <Label>Rule Scope</Label>
                <div className="mt-2 space-y-2">
                  {SCOPE_TYPES.map(scope => (
                    <label key={scope.id} className="flex items-start">
                      <input
                        type="radio"
                        name="scope"
                        value={scope.id}
                        checked={formData.scope === scope.id}
                        onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value as any }))}
                        className="mt-0.5 mr-3"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.name}</div>
                        <div className="text-xs text-gray-500">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Rule Action */}
              <div>
                <Label>Action When Rule is Violated</Label>
                <div className="mt-2 space-y-2">
                  {ACTION_TYPES.map(action => (
                    <label key={action.id} className="flex items-start">
                      <input
                        type="radio"
                        name="action"
                        value={action.id}
                        checked={formData.action === action.id}
                        onChange={(e) => setFormData(prev => ({ ...prev, action: e.target.value as any }))}
                        className="mt-0.5 mr-3"
                      />
                      <div>
                        <div className={`font-medium text-sm ${
                          action.color === 'red' ? 'text-red-700' :
                          action.color === 'yellow' ? 'text-yellow-700' :
                          'text-blue-700'
                        }`}>
                          {action.name}
                        </div>
                        <div className="text-xs text-gray-500">{action.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div>
                <Label htmlFor="priority">Priority (0 = lowest, higher numbers = higher priority)</Label>
                <Input
                  id="priority"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  placeholder="0"
                />
              </div>

              {/* Custom Message */}
              <div>
                <Label htmlFor="message">Custom Message (shown to users when rule is violated)</Label>
                <Input
                  id="message"
                  type="text"
                  value={formData.config.message || ''}
                  onChange={(e) => handleConfigChange('message', e.target.value)}
                  placeholder="Enter custom message..."
                />
              </div>

              {/* Rule-specific Configuration */}
              {renderRuleConfig()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <div>
            {step === 2 && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {step === 2 && (
              <Button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {loading ? 'Creating...' : 'Create Rule'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}